import { useEffect, useState } from 'react';
import api from '../../utils/api';
import FieldError, { errorIdFor, invalidBorder, invalidProps } from '../FieldError';
import { validationMessage } from '../../utils/formValidation';
import OrganicLoader from '../organic/OrganicLoader.jsx';
import { nextTubeCode } from './intakeSource';
import { fullRow, gridStyle, inputStyle, labelStyle, smallBtn } from './styles';
import MediumOptions from '../culture/MediumOptions';

/*
 * The fields an intake's lab material is described with, shared by the
 * intake form (IntakeForm.js) and an intake record's drawer
 * (IntakeRecordDrawer.js), which registers a plate or slants for an intake
 * later. Each component
 * takes the page's `t`, so its words come from whichever page holds it
 * (the external namespace, with plates and slants named where used).
 */

// The pure parts live in intakeSource.js; they are re-exported here so a page
// that renders these fields finds their rules in the same place.
export {
  EMPTY_PLATE_SUB, EMPTY_SLANT_SUB, defaultSlantSub, plateSubErrors, slantSubErrors,
} from './intakeSource';

/* The specimen description's vocabularies: the database ENUMs, said with the
 * collections namespace's labels. */
export const SUBSTRATES = ['live_trunk', 'dead_trunk', 'live_leaf', 'dead_leaf', 'dung', 'roots', 'soil',
  'on_another_fungus', 'carcass', 'other'];
export const SIZES = ['under_1cm', '1_to_2cm', '2_to_5cm', '5_to_10cm', 'over_10cm'];
export const STAGES = ['young', 'open', 'mature', 'old'];
export const GROWTH_PATTERNS = ['single', 'clustered', 'spread'];
export const RANKS = ['species', 'genus', 'family', 'unknown'];
export const CONFIDENCE = ['certain', 'uncertain'];

/* A field with its label above and its message below, outside the <label>
 * so the message is not read as part of the field's name. `error` is a
 * formValidation error; the control inside takes fieldProps(id, error). */
export function Field({ id, field, label, error, t, style, children }) {
  return (
    <div style={{ ...labelStyle, ...style }} data-field={field}>
      <label htmlFor={id}>{label}</label>
      {children}
      <FieldError id={errorIdFor(id)} testId={`${id}-error`}>{validationMessage(t, error)}</FieldError>
    </div>
  );
}

export const fieldProps = (id, error, extraStyle) => ({
  id,
  ...invalidProps(errorIdFor(id), !!error),
  style: { ...inputStyle, ...invalidBorder(!!error), ...extraStyle },
});

/* The protocol the taxonomy suggests for a taxon (species, then genus, then
 * family: GET /taxonomy/protocol-lookup), offered rather than applied -- the
 * person accepts it, medium and temperature together. */
export function ProtocolSuggestionBanner({ taxonName, protocols, onAccept, t }) {
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    setSuggestion(null);
    setAccepted(false);
    if (!taxonName) return;
    setLoading(true);
    api.get('/taxonomy/protocol-lookup', { params: { taxon_name: taxonName } })
      .then(res => setSuggestion(res.data.found ? res.data : null))
      .catch(() => setSuggestion(null))
      .finally(() => setLoading(false));
  }, [taxonName]);

  if (!taxonName) return null;
  if (loading) {
    return (
      <p style={{ fontSize: 12, color: 'var(--c-text-faint-fg)', ...fullRow, margin: '0 0 8px' }}>
        <OrganicLoader inline showLabel label={t('intake.protocolLookupLoading')} slowLabel={t('common:stillWorking')} />
      </p>
    );
  }
  if (!suggestion) return null;

  const matched = protocols.find(p => p.name === suggestion.protocol);
  const accept = () => {
    onAccept({
      protocol: matched ? matched.name : 'other',
      protocolOther: matched ? '' : suggestion.protocol,
      temperature: suggestion.temperature_c !== null ? suggestion.temperature_c : undefined,
    });
    setAccepted(true);
  };

  return (
    <div style={{ ...fullRow, background: 'var(--c-info-soft-2)', border: '1px solid var(--c-info-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 8, fontSize: 13, color: 'var(--c-info-3-fg)' }}>
      <div>
        {t('intake.protocolFoundMsg', {
          level: t(`common:rank.${suggestion.level}`),
          matchedName: suggestion.matched_name,
          protocol: suggestion.protocol,
        })}
      </div>
      {suggestion.temperature_c !== null ? (
        <div>{t('intake.suggestedTemp', { temp: suggestion.temperature_c })}</div>
      ) : suggestion.temperature_raw ? (
        <div style={{ color: 'var(--c-warning-text-fg)' }}>{t('intake.suggestedTempRange', { tempRaw: suggestion.temperature_raw })}</div>
      ) : null}
      {accepted ? (
        <div style={{ marginTop: 8, fontWeight: 600 }}>{t('intake.usingSuggested')}</div>
      ) : (
        <button type="button" onClick={accept} style={{ ...smallBtn('var(--c-primary)'), marginTop: 8 }} title={t('tip.acceptSuggestion')}>
          {t('intake.acceptSuggestionBtn')}
        </button>
      )}
    </div>
  );
}

/* The plate a donor's live culture goes onto: its medium (with the taxon's
 * suggestion), temperature, an optional generic control plate, and notes. */
export function PlateFields({ value, onChange, protocols, t, suggestTaxonName, errors = {}, idPrefix = 'plate' }) {
  const update = (patch) => onChange({ ...value, ...patch });
  const protocolId = `${idPrefix}-plate-protocol`;
  const otherId = `${idPrefix}-plate-protocol-other`;
  return (
    <div style={gridStyle}>
      {suggestTaxonName && (
        <ProtocolSuggestionBanner taxonName={suggestTaxonName} protocols={protocols} t={t}
          onAccept={patch => update({ protocol: patch.protocol, protocolOther: patch.protocolOther, ...(patch.temperature !== undefined ? { temperature: patch.temperature } : {}) })} />
      )}
      <Field id={protocolId} field="protocol" label={t('intake.plateProtocolLabel')} error={errors.protocol} t={t}>
        <select value={value.protocol} onChange={e => update({ protocol: e.target.value })} aria-required="true"
                {...fieldProps(protocolId, errors.protocol)} title={t('tip.plateProtocol')}>
          <option value="">{t('selectProtocol', { ns: 'plates' })}</option>
          <MediumOptions protocols={protocols} />
          <option value="other">{t('other', { ns: 'plates' })}</option>
        </select>
      </Field>
      {value.protocol === 'other' && (
        <Field id={otherId} field="protocolOther" label={t('intake.plateProtocolOtherLabel')} error={errors.protocolOther} t={t}>
          <input value={value.protocolOther} onChange={e => update({ protocolOther: e.target.value })} aria-required="true"
                 {...fieldProps(otherId, errors.protocolOther)} title={t('tip.protocolOther')} />
        </Field>
      )}
      <label style={labelStyle}>
        {t('intake.plateTemperatureLabel')}
        <input type="number" value={value.temperature} onChange={e => update({ temperature: e.target.value })} style={inputStyle} title={t('tip.plateTemperature')} />
      </label>
      <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 6, paddingBottom: 6 }}>
        <input type="checkbox" checked={value.createControlPair} onChange={e => update({ createControlPair: e.target.checked })} title={t('tip.controlPair')} />
        {t('intake.createControlPairLabel')}
      </label>
      <label style={{ ...labelStyle, ...fullRow }}>
        {t('intake.plateNotesLabel')}
        <textarea value={value.notes} onChange={e => update({ notes: e.target.value })} rows={2} style={inputStyle} title={t('tip.plateNotes')} />
      </label>
    </div>
  );
}

/* Slants registered for an intake after it was taken in (its record's
 * drawer): codes suggested from the intake's QR, a role
 * each, and the medium they grow on. Bank stock that arrives already
 * preserved is BankStockFields.js. */
export function SlantFields({ value, onChange, protocols, t, qrUid, suggestTaxonName, errors = {}, idPrefix = 'slant' }) {
  const update = (patch) => onChange({ ...value, ...patch });
  const setEntry = (i, patch) => update({ entries: value.entries.map((en, idx) => idx === i ? { ...en, ...patch } : en) });
  // The lowest S-number no row has yet: removing a middle row and adding one
  // gives its number back rather than repeating the last.
  const addEntry = () => update({ entries: [...value.entries, { code: nextTubeCode(qrUid, value.entries), role: null }] });
  const removeEntry = (i) => update({ entries: value.entries.filter((_, idx) => idx !== i) });
  const expandToStandardFour = () => update({
    entries: [
      { code: qrUid ? `${qrUid}-SA` : (value.entries[0]?.code || ''), role: 'active' },
      { code: qrUid ? `${qrUid}-SB` : '', role: 'active' },
      { code: qrUid ? `${qrUid}-SC` : '', role: 'refresh' },
      { code: qrUid ? `${qrUid}-SD` : '', role: 'backup' },
    ],
  });
  return (
    <div>
      {suggestTaxonName && (
        <div style={{ ...gridStyle, marginBottom: 0 }}>
          <ProtocolSuggestionBanner taxonName={suggestTaxonName} protocols={protocols} t={t}
            onAccept={patch => update({ protocol: patch.protocol, protocolOther: patch.protocolOther })} />
        </div>
      )}
      {value.entries.map((entry, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8, flexWrap: 'wrap' }}>
          {/* "At least one code" is said on the first. */}
          <Field id={`${idPrefix}-slant-code-${i}`} field={i === 0 ? 'slantCode' : undefined}
                 label={t('intake.slantCodeLabel', { n: i + 1 })} error={i === 0 ? errors.slantCode : null} t={t} style={{ flex: '1 1 160px' }}>
            <input value={entry.code} onChange={e => setEntry(i, { code: e.target.value })}
                   {...fieldProps(`${idPrefix}-slant-code-${i}`, i === 0 ? errors.slantCode : null)} dir="ltr" title={t('tip.slantCode')} />
          </Field>
          <label style={labelStyle}>
            {t('intake.slantRoleLabel')}
            <select value={entry.role || ''} onChange={e => setEntry(i, { role: e.target.value || null })} style={inputStyle} title={t('tip.slantRole')}>
              <option value="">{t('intake.slantRoleNone')}</option>
              <option value="active">{t('slantRole.active', { ns: 'slants' })}</option>
              <option value="refresh">{t('slantRole.refresh', { ns: 'slants' })}</option>
              <option value="backup">{t('slantRole.backup', { ns: 'slants' })}</option>
            </select>
          </label>
          {value.entries.length > 1 && (
            <button type="button" onClick={() => removeEntry(i)} title={t('tip.removeSlantRow')} aria-label={t('tip.removeSlantRow')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-faint-fg)', fontSize: 16 }}>×</button>
          )}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button type="button" onClick={addEntry} style={smallBtn('var(--c-text-muted)')} title={t('tip.addSlantRow')}>{t('intake.addSlantRowBtn')}</button>
        {value.entries.length === 1 && (
          <button type="button" onClick={expandToStandardFour} style={smallBtn('var(--c-info)')} title={t('tip.expandToFour')}>{t('intake.expandToFourBtn')}</button>
        )}
      </div>
      <label style={labelStyle}>
        {t('intake.slantProtocolLabel')}
        <select value={value.protocol} onChange={e => update({ protocol: e.target.value })} style={inputStyle} title={t('tip.slantProtocol')}>
          <option value="">{t('selectProtocol', { ns: 'plates' })}</option>
          <MediumOptions protocols={protocols} />
          <option value="other">{t('other', { ns: 'plates' })}</option>
        </select>
      </label>
      {value.protocol === 'other' && (
        <label style={labelStyle}>
          {t('intake.slantProtocolOtherLabel')}
          <input value={value.protocolOther} onChange={e => update({ protocolOther: e.target.value })} style={inputStyle} title={t('tip.protocolOther')} />
        </label>
      )}
    </div>
  );
}
